import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import financeApiService from "../../../../services/financeApiService";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";

/**
 * Gestion des N enveloppes d'un financeur (CRUD + clôture).
 *
 * La route `index` renvoie le cumul financeur (`consommation`) et la liste des
 * enveloppes, chacune avec sa propre consommation (`enveloppe.consommation`).
 */
const useEnveloppes = (financeurUuid) => {
  const [enveloppes, setEnveloppes] = useState([]);
  const [consommation, setConsommation] = useState(null);
  const [financeurConfig, setFinanceurConfig] = useState({
    etapesActives: [],
    todos: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!financeurUuid) return;
    setIsLoading(true);
    try {
      const res = await financeApiService.listerEnveloppes(financeurUuid);
      const data = financeV2Adapters.fromApi.enveloppesList(res.data);
      setEnveloppes(data.enveloppes);
      setConsommation(data.consommation);
      setFinanceurConfig(data.financeurConfig);
    } catch (err) {
      console.error("Erreur chargement enveloppes", err);
      toast.error("Erreur lors du chargement des enveloppes.");
    } finally {
      setIsLoading(false);
    }
  }, [financeurUuid]);

  const creer = useCallback(
    async (form) => {
      try {
        await financeApiService.creerEnveloppe(
          financeurUuid,
          financeV2Adapters.toApi.enveloppe(form),
        );
        toast.success("Enveloppe créée.");
        await load();
        return true;
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Erreur lors de la création de l'enveloppe.",
        );
        return false;
      }
    },
    [financeurUuid, load],
  );

  const modifier = useCallback(
    async (enveloppeUuid, form) => {
      try {
        await financeApiService.modifierEnveloppe(
          financeurUuid,
          enveloppeUuid,
          financeV2Adapters.toApi.enveloppe(form),
        );
        toast.success("Enveloppe mise à jour.");
        await load();
        return true;
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            "Erreur lors de la mise à jour de l'enveloppe.",
        );
        return false;
      }
    },
    [financeurUuid, load],
  );

  const cloturer = useCallback(
    async (enveloppeUuid) => {
      if (
        !window.confirm(
          "Clôturer cette enveloppe ? Elle ne pourra plus être sélectionnée pour de nouvelles demandes.",
        )
      ) {
        return false;
      }
      try {
        await financeApiService.cloturerEnveloppe(financeurUuid, enveloppeUuid);
        toast.success("Enveloppe clôturée.");
        await load();
        return true;
      } catch (err) {
        // 422 = enveloppe déjà clôturée.
        toast.error(
          err?.response?.data?.message || "Erreur lors de la clôture de l'enveloppe.",
        );
        return false;
      }
    },
    [financeurUuid, load],
  );

  const modifierEtapes = useCallback(
    async (enveloppeUuid, etapesActives) => {
      try {
        await financeApiService.modifierEtapesEnveloppe(
          financeurUuid,
          enveloppeUuid,
          financeV2Adapters.toApi.etapesActives(etapesActives),
        );
        await load();
        return true;
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            "Erreur lors de la mise à jour des étapes actives.",
        );
        return false;
      }
    },
    [financeurUuid, load],
  );

  const synchroniserTodos = useCallback(
    async (enveloppeUuid, todos) => {
      try {
        await financeApiService.synchroniserTodosEnveloppe(
          financeurUuid,
          enveloppeUuid,
          financeV2Adapters.toApi.todosEnveloppe(todos),
        );
        await load();
        return true;
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            "Erreur lors de la mise à jour des todos.",
        );
        return false;
      }
    },
    [financeurUuid, load],
  );

  const modifierEtapesFinanceur = useCallback(
    async (etapesActives) => {
      try {
        await financeApiService.modifierEtapesFinanceur(
          financeurUuid,
          financeV2Adapters.toApi.etapesActives(etapesActives),
        );
        await load();
        return true;
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            "Erreur lors de la mise à jour des étapes actives par défaut.",
        );
        return false;
      }
    },
    [financeurUuid, load],
  );

  const synchroniserTodosFinanceur = useCallback(
    async (todos) => {
      try {
        await financeApiService.synchroniserTodosFinanceur(
          financeurUuid,
          financeV2Adapters.toApi.todosEnveloppe(todos),
        );
        await load();
        return true;
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            "Erreur lors de la mise à jour des todos par défaut.",
        );
        return false;
      }
    },
    [financeurUuid, load],
  );

  return {
    enveloppes,
    consommation,
    financeurConfig,
    isLoading,
    load,
    creer,
    modifier,
    cloturer,
    modifierEtapes,
    synchroniserTodos,
    modifierEtapesFinanceur,
    synchroniserTodosFinanceur,
  };
};

export default useEnveloppes;
